import { FileText, RotateCcw } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { NotificationTemplate } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useResetTemplate, useSaveTemplate } from '../hooks/use-notification-mutations';
import { useTemplatePreview } from '../hooks/use-template-preview';
import {
  insertPlaceholder,
  isCustomised,
  placeholderToken,
  toOverrideText,
  unknownFromError,
  unknownPlaceholders,
} from '../schemas/placeholder-rules';
import {
  fromTemplate,
  templateFormSchema,
  toTemplateBody,
  type TemplateForm,
} from '../schemas/template-form-schema';

import { PlaceholderChips } from './placeholder-chips';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['subject', 'body', 'isEnabled']);
const PREVIEW_DELAY_MS = 400;

type TextField = 'subject' | 'body';

function Preview({
  storeId,
  template,
  subject,
  body,
  blocked,
}: {
  storeId: string;
  template: NotificationTemplate;
  subject: string;
  body: string;
  blocked: boolean;
}) {
  const { t } = useTranslation('notifications');
  const message = useErrorMessage();
  const draft = useDebouncedValue(
    useMemo(
      () => ({ subject: toOverrideText(subject), body: toOverrideText(body) }),
      [subject, body],
    ),
    PREVIEW_DELAY_MS,
  );
  const query = useTemplatePreview(storeId, template.event, draft, !blocked);
  const unknown = unknownFromError(query.error);

  return (
    <section
      aria-label={t('editor.preview')}
      className="bg-muted/30 flex min-h-40 flex-col gap-2 rounded-lg border p-4"
      data-testid="template-preview"
    >
      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {t('editor.preview')}
      </p>
      {blocked ? (
        <p className="text-muted-foreground text-sm">{t('editor.previewBlocked')}</p>
      ) : query.error ? (
        <p className="text-destructive text-sm" role="alert">
          {unknown
            ? t('editor.unknown', { names: unknown.map(placeholderToken).join(', ') })
            : message(query.error)}
        </p>
      ) : query.data ? (
        <>
          <p className="font-medium" data-testid="template-preview-subject">
            {query.data.subject}
          </p>
          <p className="text-sm whitespace-pre-line">{query.data.text}</p>
        </>
      ) : (
        <Skeleton className="h-24" />
      )}
    </section>
  );
}

export interface TemplateEditorDialogProps {
  storeId: string;
  template: NotificationTemplate;
  readOnly: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Rewords one shopper email. Blank subject or body keeps the default; placeholders come from the
 * event's list, and the preview renders the draft with sample data as you type.
 */
export function TemplateEditorDialog({
  storeId,
  template,
  readOnly,
  open,
  onOpenChange,
}: TemplateEditorDialogProps) {
  const { t } = useTranslation(['notifications', 'common']);
  const message = useErrorMessage();
  const save = useSaveTemplate(storeId);
  const reset = useResetTemplate(storeId);
  const schema = useMemo(() => templateFormSchema(template.variables), [template.variables]);
  const form = useForm<TemplateForm>({
    resolver: useI18nResolver(schema),
    defaultValues: fromTemplate(template),
  });
  const [subject, body] = useWatch({ control: form.control, name: ['subject', 'body'] });
  const unknownSubject = unknownPlaceholders(subject, template.variables);
  const unknownBody = unknownPlaceholders(body, template.variables);
  const fields = useRef<Record<TextField, HTMLInputElement | HTMLTextAreaElement | null>>({
    subject: null,
    body: null,
  });
  const lastFocused = useRef<TextField>('body');

  const close = () => {
    onOpenChange(false);
    save.reset();
    reset.reset();
  };

  const insert = (name: string) => {
    const target = lastFocused.current;
    const element = fields.current[target];
    const next = insertPlaceholder(
      form.getValues(target),
      name,
      element?.selectionStart ?? null,
      element?.selectionEnd ?? null,
    );
    form.setValue(target, next.value, { shouldDirty: true, shouldValidate: true });
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(next.caret, next.caret);
    });
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await save.mutateAsync({
        event: template.event,
        body: toTemplateBody(values, template.defaultTemplate),
      });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) {
        // Name the refused placeholders instead of the backend's sentence.
        const unknown = unknownFromError(error);
        if (unknown) {
          for (const name of ['subject', 'body'] as const) {
            if (form.getFieldState(name).error) {
              form.setError(name, {
                type: 'server',
                message: t('notifications:editor.unknown', {
                  names: unknown.map(placeholderToken).join(', '),
                }),
              });
            }
          }
        }
        return;
      }
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  const unknownHint = (names: string[]) =>
    names.length > 0 ? (
      <p className="text-destructive text-xs">
        {t('notifications:editor.unknown', { names: names.map(placeholderToken).join(', ') })}
      </p>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t(`notifications:event.${template.event}`)}</DialogTitle>
          <DialogDescription>{template.description}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-4">
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('notifications:editor.subject')}</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="off"
                          placeholder={template.defaultTemplate.subject}
                          disabled={readOnly}
                          {...field}
                          ref={(element) => {
                            field.ref(element);
                            fields.current.subject = element;
                          }}
                          onFocus={() => {
                            lastFocused.current = 'subject';
                          }}
                        />
                      </FormControl>
                      {unknownHint(unknownSubject)}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="body"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('notifications:editor.body')}</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={10}
                          className="font-mono text-[13px]"
                          placeholder={template.defaultTemplate.body}
                          disabled={readOnly}
                          {...field}
                          ref={(element) => {
                            field.ref(element);
                            fields.current.body = element;
                          }}
                          onFocus={() => {
                            lastFocused.current = 'body';
                          }}
                        />
                      </FormControl>
                      <FormDescription>{t('notifications:editor.bodyHint')}</FormDescription>
                      {unknownHint(unknownBody)}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <PlaceholderChips
                  names={template.variables}
                  onInsert={insert}
                  disabled={readOnly}
                />
                {readOnly ? null : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start"
                    onClick={() => {
                      form.setValue('subject', template.defaultTemplate.subject, {
                        shouldDirty: true,
                      });
                      form.setValue('body', template.defaultTemplate.body, { shouldDirty: true });
                    }}
                  >
                    <FileText aria-hidden />
                    {t('notifications:editor.startFromDefault')}
                  </Button>
                )}
                <FormField
                  control={form.control}
                  name="isEnabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                      <div className="grid gap-1">
                        <FormLabel>{t('notifications:editor.enabled')}</FormLabel>
                        <FormDescription>{t('notifications:editor.enabledHint')}</FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={readOnly}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <Preview
                storeId={storeId}
                template={template}
                subject={subject}
                body={body}
                blocked={unknownSubject.length > 0 || unknownBody.length > 0}
              />
            </div>
            <DialogFooter className="gap-2 sm:justify-between">
              {isCustomised(template) && !readOnly ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={reset.isPending}
                  onClick={() =>
                    reset.mutate(template.event, {
                      onSuccess: close,
                      onError: (error) =>
                        form.setError('root.server', { type: 'server', message: message(error) }),
                    })
                  }
                >
                  <RotateCcw aria-hidden />
                  {t('notifications:editor.reset')}
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={close}>
                  {t('common:actions.cancel')}
                </Button>
                {readOnly ? null : (
                  <Button type="submit" disabled={save.isPending}>
                    {save.isPending ? t('common:actions.saving') : t('notifications:editor.save')}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
